import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import {
  LogOut, Play, Copy, Check, QrCode, AlertTriangle, CheckCircle2, XCircle, ArrowUpRight, X, ShieldCheck,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

const StatusPill = ({ status }) => {
  const map = {
    active: { label: "Active", cls: "bg-[#EAF3EC] text-[#4A6D50] border-[#CDE0D3]" },
    inactive: { label: "Not subscribed", cls: "bg-[#F8F6F2] text-[#6F6B66] border-[#ECE7DF]" },
    past_due: { label: "Payment past due", cls: "bg-[#FCECEA] text-[#8A3B2E] border-[#F0C7C0]" },
    canceled: { label: "Canceled", cls: "bg-[#F8F6F2] text-[#6F6B66] border-[#ECE7DF]" },
  };
  const it = map[status] || map.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] kt-tracking uppercase ${it.cls}`}
      data-testid={`sub-status-${status}`}>
      {status === "active" ? <CheckCircle2 className="h-3 w-3" /> :
       status === "past_due" ? <AlertTriangle className="h-3 w-3" /> :
       <XCircle className="h-3 w-3" />}
      {it.label}
    </span>
  );
};

const qrUrl = (link) => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(link)}`;

export default function SalonDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [clients, setClients] = useState([]);
  const [preQuizOpen, setPreQuizOpen] = useState(false);
  const [pq, setPq] = useState({ first_name: "", email: "", consent: false });

  useEffect(() => {
    const load = async () => {
      try {
        const meRes = await axios.get(`${API}/salons/me`);
        setMe(meRes.data);
        const b = await axios.get(`${API}/salons/dashboard`);
        setBoard(b.data);
        try {
          const cl = await axios.get(`${API}/salons/clients`);
          setClients(cl.data.items || []);
        } catch {}
      } catch {
        nav("/salon/login");
      }
    };
    load();
  }, [nav]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/salons/subscription/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open subscription checkout");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of the current billing period? You'll keep access until the period ends.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/salons/subscription/cancel`);
      toast.success("Cancellation scheduled for the end of the period.");
      const b = await axios.get(`${API}/salons/dashboard`);
      setBoard(b.data);
      const meRes = await axios.get(`${API}/salons/me`);
      setMe(meRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/salons/logout`); } catch {}
    nav("/salon/login");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(board.salon_link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const openPreQuiz = () => {
    if (board?.limit_reached) {
      toast.error("Monthly quota reached — upgrade to buy more sessions.");
      return;
    }
    setPq({ first_name: "", email: "", consent: false });
    setPreQuizOpen(true);
  };

  const launchClientQuiz = () => {
    const first_name = pq.first_name.trim();
    const email = pq.email.trim();
    if (!first_name || first_name.length < 2) {
      toast.error("Client first name is required.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid client email.");
      return;
    }
    if (!pq.consent) {
      toast.error("GDPR consent is required to proceed.");
      return;
    }
    try {
      localStorage.setItem(
        "kt_salon_prequiz",
        JSON.stringify({
          first_name,
          email,
          consent: true,
          captured_at: new Date().toISOString(),
          salon_slug: board?.salon_slug || "",
        })
      );
    } catch {}
    setPreQuizOpen(false);
    // Same-tab navigation — avoids popup blockers and keeps the flow linear.
    // The salon employee returns to the dashboard via Sign In cookie after the
    // client's /plan/:id page renders.
    const target = board.salon_link || "/assessment";
    if (target.startsWith("http")) {
      window.location.href = target;
    } else {
      nav(target);
    }
  };

  if (!me || !board) {
    return <div className="min-h-screen flex items-center justify-center text-[#6F6B66] text-sm kt-tracking uppercase">…</div>;
  }

  const isActive = board.subscription_status === "active";
  const cats = Object.entries(board.product_categories || {});

  return (
    <div className="min-h-screen bg-white" data-testid="salon-dashboard">
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b kt-line">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Hairtu · Powered by KT Milano</div>
            <div className="text-sm text-[#383633] font-medium" data-testid="salon-name">{me.salon_name}</div>
          </div>
          <div className="flex items-center gap-4">
            <StatusPill status={board.subscription_status} />
            <button onClick={logout} data-testid="salon-logout"
              className="inline-flex items-center gap-1.5 text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {board.banner?.active && (board.banner?.en || board.banner?.it) && (
            <div className="rounded-2xl bg-[#F8F6F2] border kt-line p-5 text-sm text-[#383633]" data-testid="salon-banner">
              {board.banner.en || board.banner.it}
            </div>
          )}

          {!isActive ? (
            <div className="rounded-3xl bg-white border kt-line p-8 kt-shadow-md" data-testid="salon-subscribe-card">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
                {board.subscription_status === "canceled" || board.subscription_status === "past_due"
                  ? "Renew your subscription"
                  : "Activate your subscription"}
              </div>
              <h2 className="font-serif text-3xl text-[#383633] tracking-tight mb-3">
                {board.subscription_status === "canceled" || board.subscription_status === "past_due"
                  ? "Your subscription is inactive. Renew to resume client assessments."
                  : "Start unlimited client assessments."}
              </h2>
              <p className="text-[#6F6B66] mb-6">
                €{me.price_eur.toFixed(2).replace(".", ",")} / month plus applicable VAT · Monthly recurring · Cancel anytime.
              </p>
              <Button onClick={subscribe} disabled={busy} data-testid="salon-subscribe-btn"
                className="rounded-full bg-[#D8B589] hover:bg-[#C79E6E] text-[#3A2C1B] h-14 px-8 text-xs kt-tracking uppercase border border-[#C79E6E]">
                {busy ? "Opening secure checkout…"
                  : (board.subscription_status === "canceled" || board.subscription_status === "past_due")
                    ? `Renew subscription · €${me.price_eur.toFixed(2).replace(".", ",")} / month`
                    : `Subscribe · €${me.price_eur.toFixed(2).replace(".", ",")} / month`}
              </Button>
              <p className="mt-4 text-[11px] text-[#6F6B66] leading-relaxed">
                Access to client assessments is disabled until a verified payment is received.
              </p>
            </div>
          ) : (
            <div className="rounded-3xl bg-[#F8F6F2] border kt-line p-8 kt-shadow-md" data-testid="salon-start-card">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">Ready</div>
              <h2 className="font-serif text-3xl text-[#383633] tracking-tight mb-3">
                Start a new client quiz.
              </h2>
              <p className="text-[#6F6B66] mb-6">
                Opens the full Woman / Man assessment. The complete Plan appears immediately —
                no per-client paywall, since your salon subscription covers every client.
              </p>
              <div className="flex flex-wrap gap-3 mb-5">
                <button
                  type="button"
                  onClick={openPreQuiz}
                  aria-disabled={!!board.limit_reached}
                  data-testid="salon-start-quiz"
                  className={`inline-flex items-center gap-3 rounded-full ${board.limit_reached ? "bg-[#E6E1D9] text-[#8A857E] cursor-not-allowed" : "bg-[#D8B589] hover:bg-[#C79E6E] text-[#3A2C1B] hover:-translate-y-[1px]"} h-14 sm:h-16 px-8 sm:px-10 text-sm kt-tracking uppercase font-medium border border-[#C79E6E] transition-transform`}
                >
                  <Play className="h-5 w-5" /> Start Client Quiz
                </button>
                <Link
                  to="/salon/assessment"
                  data-testid="salon-start-assessment-legacy"
                  className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#F3F3F3] text-[#383633] h-14 sm:h-16 px-6 text-[11px] kt-tracking uppercase border kt-line transition-transform hover:-translate-y-[1px]"
                >
                  Legacy salon flow
                </Link>
              </div>
              {/* Session usage counter — always visible so the salon knows their remaining quota */}
              <div className="mt-4 rounded-2xl bg-white border kt-line p-4" data-testid="session-usage">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Assessments this month</div>
                  {typeof board.session_limit === "number" && board.session_limit > 0 ? (
                    <div className="text-xs font-mono text-[#383633]">
                      {board.sessions_used_period ?? 0} / {board.session_limit}
                    </div>
                  ) : (
                    <div className="text-xs font-mono text-[#383633]">
                      {board.sessions_used_period ?? board.assessments_this_month ?? 0}
                    </div>
                  )}
                </div>
                {typeof board.session_limit === "number" && board.session_limit > 0 ? (
                  <>
                    <div className="h-2 rounded-full bg-[#F3F3F3] overflow-hidden">
                      <span
                        className={`block h-full transition-all ${board.limit_reached ? "bg-[#8A3B2E]" : "bg-[#A8AD94]"}`}
                        style={{ width: `${Math.min(100, ((board.sessions_used_period ?? 0) / board.session_limit) * 100)}%` }}
                      />
                    </div>
                    {board.limit_reached && (
                      <div className="mt-2 text-[11px] text-[#8A3B2E]">
                        Monthly quota reached — please upgrade to keep assessing clients this month.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[11px] text-[#6F6B66]">Unlimited on your current plan.</div>
                )}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Stat title="Total assessments" value={board.total_assessments} testId="stat-total" />
            <Stat title="This month" value={board.assessments_this_month} testId="stat-month" />
            <Stat title="Woman path" value={board.women_path} testId="stat-women" />
            <Stat title="Man path" value={board.men_path} testId="stat-men" />
            <Stat title="Shopify clicks" value={board.shopify_link_clicks} testId="stat-clicks" />
            <Stat title="Attributed purchases" value={board.attributed_purchases} testId="stat-purchases" />
          </div>

          <div className="rounded-2xl bg-white border kt-line p-6" data-testid="product-categories">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
              Product categories recommended
            </div>
            {cats.length === 0 ? (
              <div className="text-sm text-[#6F6B66]">No assessments yet.</div>
            ) : (
              <ul className="space-y-2">
                {cats.map(([cat, n]) => (
                  <li key={cat} className="flex items-center justify-between text-sm">
                    <span className="text-[#383633] capitalize">{cat.replace(/[_-]/g, " ")}</span>
                    <span className="text-[#6F6B66] font-mono">{n}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="rounded-2xl bg-white border kt-line p-6" data-testid="subscription-card">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Subscription</div>
            <div className="text-sm text-[#383633] mb-3">
              €{me.price_eur.toFixed(2).replace(".", ",")} / month · plus applicable VAT
            </div>
            {board.next_billing_date && (
              <div className="text-xs text-[#6F6B66] mb-3" data-testid="next-billing">
                {board.cancel_at_period_end
                  ? `Access ends on ${new Date(board.next_billing_date).toLocaleDateString()}`
                  : `Next billing: ${new Date(board.next_billing_date).toLocaleDateString()}`}
              </div>
            )}
            {isActive && !board.cancel_at_period_end && (
              <button onClick={cancel} disabled={busy} data-testid="cancel-subscription"
                className="text-xs kt-tracking uppercase text-[#8A3B2E] hover:underline">
                Cancel at period end
              </button>
            )}
            {board.cancel_at_period_end && (
              <div className="text-xs text-[#8A3B2E] mt-1" data-testid="cancel-scheduled">
                Cancellation scheduled
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white border kt-line p-6 text-center" data-testid="referral-link">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3 flex items-center justify-center gap-1.5">
              <QrCode className="h-3.5 w-3.5" /> Your salon link
            </div>
            <img
              alt="Salon QR"
              src={qrUrl(board.salon_link)}
              loading="lazy"
              decoding="async"
              className="mx-auto rounded-xl border kt-line"
              data-testid="salon-qr"
            />
            <div className="text-[11px] text-[#383633] break-all mt-4 mb-3">{board.salon_link}</div>
            <Button variant="outline" onClick={copyLink} data-testid="copy-salon-link"
              className="rounded-full border-[#383633] text-[#383633] hover:bg-[#F8F6F2] text-xs kt-tracking uppercase h-10">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              <span className="ml-2">{copied ? "Copied" : "Copy link"}</span>
            </Button>
          </div>
        </div>

        {/* Clients table — every client the salon has assessed (name · email · date · result link) */}
        {isActive && clients.length > 0 && (
          <div className="mt-14" data-testid="salon-clients">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Clients</div>
            <h2 className="font-serif text-3xl text-[#383633] tracking-tight mb-4">Your client assessments</h2>
            <div className="rounded-2xl border kt-line bg-white kt-shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F6F2] text-[#6F6B66] text-[10px] kt-tracking-wider uppercase">
                  <tr>
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3 hidden sm:table-cell">Email</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Path</th>
                    <th className="text-left px-5 py-3">Date</th>
                    <th className="text-right px-5 py-3">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.result_id} className="border-t kt-line hover:bg-[#F8F6F2]/60" data-testid={`salon-client-${c.result_id}`}>
                      <td className="px-5 py-3 text-[#383633] font-medium">{c.first_name || "—"}</td>
                      <td className="px-5 py-3 text-[#6F6B66] hidden sm:table-cell truncate max-w-[220px]">{c.email || "—"}</td>
                      <td className="px-5 py-3 text-[#6F6B66] hidden md:table-cell uppercase text-[10px] kt-tracking">{c.flow || "female"} · {c.language || "en"}</td>
                      <td className="px-5 py-3 text-[#6F6B66] font-mono text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <a
                          href={`/plan/${c.result_id}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] kt-tracking uppercase text-[#383633] hover:text-[#4A4238]"
                          data-testid={`salon-client-view-${c.result_id}`}
                        >
                          View <ArrowUpRight className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pre-quiz capture modal — Client name + email + GDPR consent */}
      {preQuizOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#383633]/40 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="prequiz-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreQuizOpen(false); }}
        >
          <div className="w-full max-w-md rounded-3xl bg-white kt-shadow-lg p-6 sm:p-8 relative">
            <button
              type="button"
              onClick={() => setPreQuizOpen(false)}
              data-testid="prequiz-close"
              aria-label="Close"
              className="absolute top-3 right-3 h-9 w-9 rounded-full hover:bg-[#F3F3F3] inline-flex items-center justify-center text-[#6F6B66]"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Client details</div>
            <h3 className="font-serif text-2xl text-[#383633] tracking-tight mb-2">Start a new client assessment</h3>
            <p className="text-sm text-[#6F6B66] leading-relaxed mb-5">
              We store your client's first name + email to attribute this assessment to your salon
              and let you view their result later. Your salon acts as the Data Controller for this
              information under GDPR.
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="pq-name" className="text-xs kt-tracking uppercase text-[#6F6B66]">
                  Client first name
                </Label>
                <Input
                  id="pq-name"
                  data-testid="prequiz-name"
                  value={pq.first_name}
                  autoFocus
                  onChange={(e) => setPq({ ...pq, first_name: e.target.value })}
                  className="mt-2 border-[#ECE7DF] rounded-xl h-11"
                  placeholder="e.g. Giulia"
                />
              </div>
              <div>
                <Label htmlFor="pq-email" className="text-xs kt-tracking uppercase text-[#6F6B66]">
                  Client email
                </Label>
                <Input
                  id="pq-email"
                  type="email"
                  data-testid="prequiz-email"
                  value={pq.email}
                  onChange={(e) => setPq({ ...pq, email: e.target.value })}
                  className="mt-2 border-[#ECE7DF] rounded-xl h-11"
                  placeholder="client@example.com"
                />
              </div>
              <label className="flex items-start gap-3 pt-1 cursor-pointer">
                <Checkbox
                  id="pq-consent"
                  data-testid="prequiz-consent"
                  checked={pq.consent}
                  onCheckedChange={(v) => setPq({ ...pq, consent: !!v })}
                  className="mt-0.5"
                />
                <span className="text-[12px] text-[#383633] leading-relaxed">
                  <ShieldCheck className="h-3.5 w-3.5 inline-block mr-1 text-[#A8AD94] align-[-2px]" />
                  I confirm my client has given consent to store their first name and email so their
                  personalized result can be attributed to this salon. Client data is never used for
                  KT Milano marketing.
                </span>
              </label>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setPreQuizOpen(false)}
                data-testid="prequiz-cancel"
                className="rounded-full h-11 px-5 text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]"
              >
                Cancel
              </button>
              <Button
                onClick={launchClientQuiz}
                data-testid="prequiz-launch"
                className="rounded-full bg-[#D8B589] hover:bg-[#C79E6E] text-[#3A2C1B] h-11 px-6 text-xs kt-tracking uppercase border border-[#C79E6E]"
              >
                <Play className="h-4 w-4 mr-1.5" /> Launch assessment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ title, value, testId }) {
  return (
    <div className="rounded-2xl bg-white border kt-line p-5" data-testid={testId}>
      <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{title}</div>
      <div className="font-serif text-3xl text-[#383633] mt-1">{value}</div>
    </div>
  );
}
